import React, { useState, useEffect, useCallback, lazy, Suspense, useRef } from 'react';
import '@pages/newtab/Newtab.css';
import TimeDisplay from '../../components/TimeDisplay';
import {
  getDefaultBackground,
  getRandomGradient,
  getNextBackground,
  WalpaperType,
  preloadImage,
  getWallpapers,
  hydrateCustomBackground,
  normalizeBackground,
  serializeBackground,
  updateWallpapers,
} from '../../services/backgroundService';
import { User } from '../../services/types';
import { UserCenterRef } from '../../components/UserCenter';
import { AIChatRef } from '../../components/AIChatComponent';
import SearchInput from '../../components/SearchInput';
import Cloud from '@src/assets/img/cloud.webp';
import { I18nProvider } from '../../i18n/I18nContext';
import { isBrowserExtensionMode } from '../../services/browserAPI';
import {
  releaseAllCustomBackgroundUrls,
  releaseUnusedCustomBackgroundUrls,
} from '../../services/customBackgroundStorage';
// 直接导入 WeatherDisplay - 改为懒加载
const WeatherDisplay = lazy(() => import('../../components/WeatherDisplay'));

// 使用浏览器API兼容层来确定是否为扩展环境
const isExtensionEnv = isBrowserExtensionMode();

const BookmarkButton = lazy(() => import('../../components/BookmarkButton'));
const UserCenter = lazy(() => import('../../components/UserCenter'));
const NextBackgroundButton = lazy(() => import('../../components/NextBackgroundButton'));
const Subscription = lazy(() => import('../../components/Subscription'));
const Toast = lazy(() => import('../../components/Toast'));
const TopSitesComponent = lazy(() => import('../../components/TopSitesComponent'));
const TodoButton = lazy(() => import('../../components/TodoButton'));
const AIChatComponent = lazy(() => import('../../components/AIChatComponent'));
type ToastProps = React.ComponentProps<typeof Toast>;

const BACKGROUND_KEY = 'lastUsedBackground';
const AUTO_BACKGROUND_NEXT_CHANGE_KEY = 'autoBackgroundNextChangeAt';
const MAX_FREE_BACKGROUND_CHANGES = 1; // 设置免费更换背景的最大次数
const BACKGROUND_TRANSITION_DURATION = 1200;
const MAX_TIMEOUT_DELAY = 2_147_483_647;

const homeComponentTransition = (visible: boolean) =>
  `home-component-transition ${visible
    ? 'home-component-transition-visible'
    : 'home-component-transition-hidden'}`;

const getStoredIntervalValue = (key: string, fallback: number) => {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
};

const getAutoBackgroundIntervalMs = (days: number, hours: number, minutes: number) =>
  (days * 24 * 60 + hours * 60 + minutes) * 60 * 1000;

const preloadVideo = (src: string): Promise<void> => new Promise((resolve, reject) => {
  const video = document.createElement('video');
  const timeout = window.setTimeout(() => finish(new Error('Video loading timeout')), 15_000);

  const cleanup = () => {
    window.clearTimeout(timeout);
    video.onloadedmetadata = null;
    video.oncanplaythrough = null;
    video.onerror = null;
    video.removeAttribute('src');
    video.load();
  };
  const finish = (error?: Error) => {
    cleanup();
    if (error) reject(error);
    else resolve();
  };

  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.preload = 'auto';
  video.onloadedmetadata = () => finish();
  video.oncanplaythrough = () => finish();
  video.onerror = () => finish(new Error(`Loading video error: ${video.error?.message ?? 'Unknown error'}`));
  video.src = src;
});

const Newtab: React.FC = () => {
  const userCenterRef = useRef<UserCenterRef>(null);
  const aiChatRef = useRef<AIChatRef>(null);
  const transitionTimerRef = useRef<number | null>(null);
  const autoBackgroundTimerRef = useRef<number | null>(null);
  const handleBackgroundChangeRef = useRef<(background?: WalpaperType) => Promise<void>>(async () => {});
  const [background, setBackground] = useState<WalpaperType | null>(null);
  const [previousBackground, setPreviousBackground] = useState<WalpaperType | null>(null);
  const [isBackgroundTransitioning, setIsBackgroundTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [fadeIn, setFadeIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showButtons, setShowButtons] = useState(false);
  const [backgroundChangeCount, setBackgroundChangeCount] = useState(0);
  const [showSubscription, setShowSubscription] = useState(false);
  const [toast, setToast] = useState<ToastProps | null>(null);
  const [wallpapers, setWallpapers] = useState<WalpaperType[]>([]);
  const [showTime, setShowTime] = useState(() => localStorage.getItem('showTime') !== 'false');
  const [showSeconds, setShowSeconds] = useState(() => localStorage.getItem('showSeconds') === 'true');
  const [showBookmark, setShowBookmark] = useState(() => 
    localStorage.getItem('showBookmark') !== 'false' // 默认显示书签按钮
  );
  const [showNextBackground, setShowNextBackground] = useState(() => localStorage.getItem('showNextBackground') !== 'false');
  const [showSearch, setShowSearch] = useState(() => localStorage.getItem('showSearch') !== 'false');
  const [showWeather, setShowWeather] = useState(() => localStorage.getItem('showWeather') !== 'false'); // Add new state
  const [showTopSites, setShowTopSites] = useState(() => localStorage.getItem('showTopSites') !== 'false');
  const [autoChangeBackground, setAutoChangeBackground] = useState(() => localStorage.getItem('autoChangeBackground') === 'true');
  const [autoBackgroundDays, setAutoBackgroundDays] = useState(() => getStoredIntervalValue('autoBackgroundDays', 0));
  const [autoBackgroundHours, setAutoBackgroundHours] = useState(() => getStoredIntervalValue('autoBackgroundHours', 0));
  const [autoBackgroundMinutes, setAutoBackgroundMinutes] = useState(() => getStoredIntervalValue('autoBackgroundMinutes', 30));
  const [syncEnabled, setSyncEnabled] = useState(() => localStorage.getItem('syncEnabled') !== 'false');
  const [showAIChat, setShowAIChat] = useState(() => localStorage.getItem('showAIChat') !== 'false');
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [skipGreeting, setSkipGreeting] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const [isPageEntering, setIsPageEntering] = useState(true);

  useEffect(() => {
    if (!background || isPageReady) return;

    const animationFrame = window.requestAnimationFrame(() => {
      setIsPageReady(true);
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [background, isPageReady]);

  useEffect(() => {
    if (!isPageReady) return;

    const entryTimer = window.setTimeout(() => setIsPageEntering(false), 420);
    return () => window.clearTimeout(entryTimer);
  }, [isPageReady]);

  const handleSettingsChange = (settings: {
    showBookmark: boolean;
    showTime: boolean;
    showSeconds: boolean;
    showNextBackground: boolean;
    showAIChat: boolean;
    showSearch: boolean;
    showWeather: boolean; // Add new setting
    showTopSites: boolean; // Add new setting
    autoChangeBackground: boolean;
    autoBackgroundDays: number;
    autoBackgroundHours: number;
    autoBackgroundMinutes: number;
    syncEnabled: boolean;
  }) => {
    const previousInterval = getAutoBackgroundIntervalMs(autoBackgroundDays, autoBackgroundHours, autoBackgroundMinutes);
    const nextInterval = getAutoBackgroundIntervalMs(
      settings.autoBackgroundDays,
      settings.autoBackgroundHours,
      settings.autoBackgroundMinutes
    );

    setShowBookmark(settings.showBookmark);
    setShowTime(settings.showTime);
    setShowSeconds(settings.showSeconds);
    setShowNextBackground(settings.showNextBackground);
    setShowAIChat(settings.showAIChat);
    setShowSearch(settings.showSearch);
    setShowWeather(settings.showWeather); // Set new state
    setShowTopSites(settings.showTopSites); // Set new state
    setAutoChangeBackground(settings.autoChangeBackground);
    setAutoBackgroundDays(settings.autoBackgroundDays);
    setAutoBackgroundHours(settings.autoBackgroundHours);
    setAutoBackgroundMinutes(settings.autoBackgroundMinutes);
    setSyncEnabled(settings.syncEnabled);
    localStorage.setItem('showBookmark', settings.showBookmark.toString());
    localStorage.setItem('showTime', settings.showTime.toString());
    localStorage.setItem('showSeconds', settings.showSeconds.toString());
    localStorage.setItem('showNextBackground', settings.showNextBackground.toString());
    localStorage.setItem('showAIChat', settings.showAIChat.toString());
    localStorage.setItem('showSearch', settings.showSearch.toString());
    localStorage.setItem('showWeather', settings.showWeather.toString()); // Store in localStorage
    localStorage.setItem('showTopSites', settings.showTopSites.toString()); // Store in localStorage
    localStorage.setItem('autoChangeBackground', settings.autoChangeBackground.toString());
    localStorage.setItem('autoBackgroundDays', settings.autoBackgroundDays.toString());
    localStorage.setItem('autoBackgroundHours', settings.autoBackgroundHours.toString());
    localStorage.setItem('autoBackgroundMinutes', settings.autoBackgroundMinutes.toString());
    localStorage.setItem('syncEnabled', settings.syncEnabled.toString());

    if (!settings.autoChangeBackground) {
      localStorage.removeItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY);
    } else if (!autoChangeBackground || previousInterval !== nextInterval) {
      if (nextInterval > 0) {
        localStorage.setItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY, String(Date.now() + nextInterval));
      } else {
        localStorage.removeItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadInitialBackground = async () => {
      const savedBackground = localStorage.getItem(BACKGROUND_KEY);
      if (savedBackground) {
        try {
          const parsedBackground = JSON.parse(savedBackground) as WalpaperType;
          const hydratedBackground = parsedBackground.type === 'custom'
            ? await hydrateCustomBackground(parsedBackground)
            : normalizeBackground(parsedBackground);
          if (!cancelled) {
            setBackground(hydratedBackground);
            if (hydratedBackground !== parsedBackground) {
              localStorage.setItem(BACKGROUND_KEY, JSON.stringify(hydratedBackground));
            }
          }
        } catch (error) {
          console.error('Error loading saved background:', error);
          loadDefaultBackground();
        }
      } else {
        loadDefaultBackground();
      }
    };

    const loadDefaultBackground = () => {
      if (!cancelled) setBackground(getDefaultBackground());
    };

    loadInitialBackground();

    // const timer = setTimeout(() => setShowTime(true), 0);
    const timerDelayButtons = setTimeout(() => setShowButtons(true), 0);
    return () => {
      cancelled = true;
      releaseAllCustomBackgroundUrls();
      // clearTimeout(timer);
      clearTimeout(timerDelayButtons);
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const handleBackgroundChange = async (newBackground?: WalpaperType) => {
    if (background?.raw === newBackground?.raw) return; // 仅当 URL 相同时才返回
    
    setIsLoading(true);
    try {
      let backgroundToSet: WalpaperType;
      let isNewBackground = false;

      if (newBackground) {
        // 如果是已有壁纸，直接设置
        backgroundToSet = newBackground;
      } else {
        // 如果是获取新壁纸，先检查限制
        if (currentUser?.plan_name === 'Free' && backgroundChangeCount >= MAX_FREE_BACKGROUND_CHANGES) {
          setShowSubscription(true);
          setIsLoading(false);
          return;
        }
        
        // 通过API获取新壁纸
        backgroundToSet = await getNextBackground();
        isNewBackground = true;
      }
      
      // Skip the transition when the source has no different background available.
      if (background?.raw === backgroundToSet.raw) return;

      if (backgroundToSet.type === 'custom' && backgroundToSet.customType === 'video') {
        await preloadVideo(backgroundToSet.raw);
      } else if (!backgroundToSet.isGradient && !backgroundToSet.isAnimated) {
        await preloadImage(backgroundToSet.raw);
      }
      
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }

      setPreviousBackground(background);
      setIsBackgroundTransitioning(true);
      setBackground(backgroundToSet);

      transitionTimerRef.current = window.setTimeout(() => {
        setPreviousBackground(null);
        setIsBackgroundTransitioning(false);
        releaseUnusedCustomBackgroundUrls(
          backgroundToSet.type === 'custom' ? [backgroundToSet.raw] : []
        );
        transitionTimerRef.current = null;
      }, BACKGROUND_TRANSITION_DURATION);
      
      try {
        const storageData = serializeBackground(backgroundToSet);
        localStorage.setItem(BACKGROUND_KEY, JSON.stringify(storageData));
      } catch (error) {
        console.error('Storage error:', error);
        showToast('Insufficient local storage');
      }
      
      if (isNewBackground && backgroundToSet.type === 'image') {
        setBackgroundChangeCount(prevCount => prevCount + 1);
        setWallpapers(prevWallpapers => updateWallpapers(backgroundToSet, prevWallpapers, true));
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : (error as Event).type === 'error' ? 'Video resource loading failed' : 'Unknown error';
      console.error('Error loading new background:', error);
      showToast(`Background loading failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  handleBackgroundChangeRef.current = handleBackgroundChange;

  useEffect(() => {
    if (autoBackgroundTimerRef.current !== null) {
      window.clearTimeout(autoBackgroundTimerRef.current);
      autoBackgroundTimerRef.current = null;
    }

    if (!autoChangeBackground) return;

    const intervalMs = getAutoBackgroundIntervalMs(autoBackgroundDays, autoBackgroundHours, autoBackgroundMinutes);
    if (intervalMs <= 0) return;

    let cancelled = false;

    const scheduleNextChange = () => {
      if (cancelled) return;

      let nextChangeAt = Number(localStorage.getItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY));
      if (!Number.isFinite(nextChangeAt) || nextChangeAt <= 0) {
        nextChangeAt = Date.now() + intervalMs;
        localStorage.setItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY, String(nextChangeAt));
      }

      const delay = Math.min(Math.max(nextChangeAt - Date.now(), 0), MAX_TIMEOUT_DELAY);
      autoBackgroundTimerRef.current = window.setTimeout(async () => {
        if (cancelled) return;

        const storedNextChangeAt = Number(localStorage.getItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY));
        if (Number.isFinite(storedNextChangeAt) && storedNextChangeAt > Date.now()) {
          scheduleNextChange();
          return;
        }

        // 先推进共享时间戳，避免多个同时打开的新标签页重复触发切换。
        localStorage.setItem(AUTO_BACKGROUND_NEXT_CHANGE_KEY, String(Date.now() + intervalMs));
        try {
          if (background?.type === 'image') {
            await handleBackgroundChangeRef.current();
          } else if (background?.type === 'gradient') {
            const nextGradient = getRandomGradient(background.raw);
            if (nextGradient) {
              await handleBackgroundChangeRef.current(nextGradient);
            }
          }
          // Animated and custom categories currently contain one selected item,
          // so keeping it avoids unexpectedly switching to another category.
        } finally {
          scheduleNextChange();
        }
      }, delay);
    };

    scheduleNextChange();

    return () => {
      cancelled = true;
      if (autoBackgroundTimerRef.current !== null) {
        window.clearTimeout(autoBackgroundTimerRef.current);
        autoBackgroundTimerRef.current = null;
      }
    };
  }, [autoChangeBackground, autoBackgroundDays, autoBackgroundHours, autoBackgroundMinutes, background]);


  const loadWallpapers = async () => {
    setIsLoading(true);
    try {
      const loadedWallpapers = await getWallpapers();
      setWallpapers(loadedWallpapers);
    } catch (error) {
      console.error('Error loading wallpapers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserUpdate = (user: User | null) => {
    setCurrentUser(user);
  };

  const refreshUserInfo = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const response = await fetch('https://api.tabbyio.com/get_user_info', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const userInfo = await response.json();
        setCurrentUser(userInfo);
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        localStorage.setItem('user_info_expiry', (Date.now() + 30 * 60 * 1000).toString());
      }
    } catch (error) {
      console.error('Refresh user info failed:', error);
    }
  }, []);

  const showToast = (message: string, type: ToastProps['type'] = 'pure') => {
    setToast({ message, type, duration: 3000, autoHide: true });
  };

  const handleNextBackgroundClick = () => {
    if (!currentUser) {
      userCenterRef.current?.openWith('Profile');
      return;
    }

    handleBackgroundChange();
  };

  const renderBackgroundLayer = (
    layerBackground: WalpaperType,
    className = '',
    layerKey?: string
  ) => {
    if (layerBackground.type === 'custom') {
      return (
        <div key={layerKey} className={`background-transition-layer ${className}`}>
          {layerBackground.customType === 'video' ? (
            <video 
              src={layerBackground.raw}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              src={layerBackground.raw}
              className="w-full h-full object-cover"
              alt="DIY Background"
            />
          )}
        </div>
      );
    }
    if (layerBackground.type === 'animated' && layerBackground.animation?.type === 'cloud') {
      return (
        <div key={layerKey} className={`background-transition-layer bg-[#AFD1F6] overflow-hidden ${className}`}>
          <div className="animate-scroll will-change-transform h-[200%]">
            <img 
              alt="clouds" 
              src={Cloud}
              className="h-full w-full object-cover"
              style={{ transform: 'translateZ(0)' }} // 添加硬件加速
            />
          </div>
        </div>
      );
    }
    return (
      <div
        key={layerKey}
        className={`background-transition-layer !bg-cover !bg-center ${className}`}
        style={{
          background: layerBackground.type === 'gradient'
            ? layerBackground.raw
            : `url(${layerBackground.raw})`
        }}
      />
    );
  };

  return (
    <I18nProvider>
      <div className={`newtab-page w-screen h-screen flex relative overflow-hidden pb-safe ${
        isPageReady ? 'newtab-page-ready' : ''
      }`}>
        {/* Keep the outgoing layer mounted while the incoming layer fades in. */}
        {previousBackground && renderBackgroundLayer(
          previousBackground,
          'background-transition-exit',
          `previous-${previousBackground.type}-${previousBackground.raw}`
        )}
        {background && renderBackgroundLayer(
          background,
          `${isPageEntering ? 'newtab-background-enter' : ''} ${
            isBackgroundTransitioning ? 'background-transition-enter' : ''
          }`,
          `current-${background.type}-${background.raw}`
        )}
        
        {/* Main content */}
        <div className="newtab-content relative w-full flex">
          {/* 左侧栏 */}
          <div className="flex flex-col w-1/4 sm:w-1/4 p-1 max-sm:w-1/5">
            <div className="flex-grow">
              <Suspense fallback={null}>
                {isExtensionEnv && (
                  <div
                    className={homeComponentTransition(showButtons && showBookmark)}
                    aria-hidden={!(showButtons && showBookmark)}
                  >
                    <BookmarkButton />
                  </div>
                )}
              </Suspense>
            </div>
            <div className={`${isAIChatOpen ? 'max-sm:hidden' : ''}`}>
              <Suspense fallback={null}>
                <UserCenter
                  ref={userCenterRef}
                  onBackgroundSelect={handleBackgroundChange}
                  currentBackground={background}
                  onUserUpdate={handleUserUpdate}
                  onShowSubscription={() => setShowSubscription(true)}
                  showToast={showToast}
                  isLoadingWallpapers={isLoading}
                  wallpapers={wallpapers}
                  loadWallpapers={loadWallpapers}
                  currentUser={currentUser}
                  settings={{
                    showBookmark,
                    showTime,
                    showSeconds,
                    showNextBackground,
                    showAIChat,
                    showSearch,
                    showWeather,
                    showTopSites,
                    autoChangeBackground,
                    autoBackgroundDays,
                    autoBackgroundHours,
                    autoBackgroundMinutes,
                    syncEnabled,
                  }}
                  onSettingsChange={handleSettingsChange}
                  isExtensionEnvironment={Boolean(isExtensionEnv)}
                />
              </Suspense>
            </div>
          </div>

          {/* 中间区域 */}
          <div className="flex flex-col flex-grow items-center justify-between p-1">
            <div className="flex-grow flex flex-col items-center justify-start pt-12 gap-8">
              <div
                className={homeComponentTransition(showTime)}
                aria-hidden={!showTime}
              >
                <TimeDisplay show={showTime} showSeconds={showSeconds} />
              </div>
            </div>
            <Suspense fallback={null}>
              <div
                className={homeComponentTransition(showButtons && showAIChat)}
                aria-hidden={!(showButtons && showAIChat)}
              >
                {showButtons && (
                  <AIChatComponent
                    ref={aiChatRef}
                    currentUser={currentUser}
                    showToast={showToast}
                    onShowSubscription={() => setShowSubscription(true)}
                    onNotLoggedIn={() => userCenterRef.current?.openWith('Profile')}
                    skipGreeting={skipGreeting}
                    onOpenChange={(isOpen) => {
                      setIsAIChatOpen(isOpen);
                      if (!isOpen) {
                        setSkipGreeting(false);
                      }
                    }}
                  />
                )}
              </div>
            </Suspense>
          </div>

          {/* Search Input */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 sm:w-auto w-full px-2 max-sm:top-[40%]">
            <div className={homeComponentTransition(showSearch && !isAIChatOpen)} aria-hidden={!showSearch || isAIChatOpen}>
              <SearchInput onAIChat={(message) => {
                if (!currentUser) {
                  userCenterRef.current?.openWith('Profile');
                  return;
                }
                setShowButtons(true);
                setSkipGreeting(true);
                const aiChatComponent = document.querySelector('[data-ai-chat-button]');
                if (aiChatComponent) {
                  (aiChatComponent as HTMLButtonElement).click();
                  setTimeout(() => {
                    aiChatRef.current?.sendMessage(message);
                  }, 200);
                }
              }} show={showSearch && !isAIChatOpen} />
            </div>
          </div>

          {/* Top Sites Component - Add below search */}
          <div className="absolute top-[60%] left-1/2 transform -translate-x-1/2">
            <div
              className={homeComponentTransition(showButtons && showTopSites && !isAIChatOpen)}
              aria-hidden={!(showButtons && showTopSites) || isAIChatOpen}
            >
              <Suspense fallback={null}>
                {showButtons && isExtensionEnv && (
                  <TopSitesComponent show />
                )}
              </Suspense>
            </div>
          </div>

          {/* 右侧栏 */}
          <div className="flex flex-col w-1/4 sm:w-1/4 p-1 max-sm:w-1/5">
            <div className="flex-grow">
              <Suspense fallback={null}>
                <div
                  className={homeComponentTransition(showWeather)}
                  aria-hidden={!showWeather}
                >
                  <WeatherDisplay className="float-right mb-4" />
                </div>
              </Suspense>
            </div>
            <div className={`${isAIChatOpen ? 'max-sm:hidden' : ''}`}>
              <Suspense fallback={null}>
                <div className="flex items-center justify-end gap-1">
                  <div
                    className={homeComponentTransition(showNextBackground)}
                    aria-hidden={!showNextBackground}
                  >
                    <NextBackgroundButton onClick={handleNextBackgroundClick} isLoading={isLoading} />
                  </div>
                  <TodoButton onProRequired={() => userCenterRef.current?.openWith('Rewards')} />
                </div>
              </Suspense>
            </div>
          </div>
        </div>

        {/* Modals and Toasts */}
        <Suspense fallback={null}>
          <Subscription
            isOpen={showSubscription}
            onClose={() => setShowSubscription(false)}
            onRefreshUser={refreshUserInfo}
            onShowRewards={() => {
              setShowSubscription(false);
              userCenterRef.current?.openWith('Rewards');
            }}
            currentUser={currentUser}
            showToast={showToast}
          />
        </Suspense>

        {toast && (
          <Suspense fallback={null}>
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
              duration={toast.duration}
              autoHide={toast.autoHide}
            />
          </Suspense>
        )}
      </div>
    </I18nProvider>
  );
};

export default Newtab;
